import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Braces, PencilLine, Plus, Save, X } from 'lucide-react';
import { api } from '../../api';
import './directory-field-schemas-administration.css';

type Schema = {
  id: string;
  entity_type: 'COMPANY' | 'CONTACT';
  field_key: string;
  label: string;
  value_type: 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'DATETIME' | 'ENUM' | 'MULTI_ENUM' | 'URL';
  owner: 'PORTAL' | 'BITRIX24';
  bitrix_field_name?: string | null;
  enum_options?: unknown[];
  is_required: boolean;
  is_active: boolean;
};

type SchemaForm = {
  entityType: Schema['entity_type'];
  fieldKey: string;
  label: string;
  valueType: Schema['value_type'];
  owner: Schema['owner'];
  bitrixFieldName: string;
  enumOptions: string;
  isRequired: boolean;
  isActive: boolean;
};

const EMPTY: SchemaForm = {
  entityType: 'COMPANY',
  fieldKey: '',
  label: '',
  valueType: 'TEXT',
  owner: 'PORTAL',
  bitrixFieldName: '',
  enumOptions: '',
  isRequired: false,
  isActive: true,
};

export function parseDirectoryEnumOptions(value: string) {
  return [...new Set(value.split(/[\n,]/).map(item => item.trim()).filter(Boolean))];
}

function editForm(schema: Schema) {
  return {
    entityType: schema.entity_type,
    fieldKey: schema.field_key,
    label: schema.label,
    valueType: schema.value_type,
    owner: schema.owner,
    bitrixFieldName: schema.bitrix_field_name || '',
    enumOptions: (schema.enum_options || []).map(item => String(typeof item === 'object' && item !== null ? (item as { value?: unknown }).value ?? '' : item)).filter(Boolean).join('\n'),
    isRequired: schema.is_required,
    isActive: schema.is_active,
  };
}

export function DirectoryFieldSchemasAdministration() {
  const queryClient = useQueryClient();
  const { data: queryData = [], isLoading } = useQuery({ queryKey: ['directory-field-schemas'], queryFn: () => api<Schema[]>('/directory/field-schemas') });
  const data = Array.isArray(queryData) ? queryData : [];
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api<Schema>(editingId ? `/directory/field-schemas/${editingId}` : '/directory/field-schemas', { method: editingId ? 'PATCH' : 'POST', body: JSON.stringify(payload) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['directory-field-schemas'] });
      await queryClient.invalidateQueries({ queryKey: ['directory-fields'] });
      setMessage(editingId ? 'Поле обновлено' : 'Поле создано');
      setError('');
      setEditingId(null);
      setForm(EMPTY);
      setOpen(false);
    },
    onError: reason => { setMessage(''); setError(reason instanceof Error ? reason.message : 'Не удалось сохранить поле'); },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate({
      entityType: form.entityType,
      fieldKey: form.fieldKey.trim(),
      label: form.label.trim(),
      valueType: form.valueType,
      owner: form.owner,
      bitrixFieldName: form.owner === 'BITRIX24' ? form.bitrixFieldName.trim() || undefined : undefined,
      enumOptions: ['ENUM', 'MULTI_ENUM'].includes(form.valueType) ? parseDirectoryEnumOptions(form.enumOptions) : [],
      isRequired: form.isRequired,
      isActive: form.isActive,
    });
  }
  function startEdit(schema: Schema) {
    setEditingId(schema.id);
    setForm(editForm(schema));
    setMessage('');
    setError('');
    setOpen(true);
  }
  return <section className="surface directory-schema-admin">
    <header><div className="directory-schema-admin__title"><span><Braces /></span><div><h3>Пользовательские поля справочников</h3><p>Тип, владелец данных и сопоставление с полями Битрикс24</p></div></div><button className="button ghost" onClick={() => { setEditingId(null); setForm(EMPTY); setOpen(value => !value); setError(''); }}>{open ? <X /> : <Plus />}{open ? 'Закрыть' : 'Добавить поле'}</button></header>
    {(message || error) && <div className={`admin-feedback ${error ? 'error' : 'success'}`}>{error || message}</div>}
    {open && <form className="directory-schema-admin__form" onSubmit={submit}>
      <label>Справочник<select value={form.entityType} onChange={event => setForm(value => ({ ...value, entityType: event.target.value as typeof value.entityType }))} disabled={Boolean(editingId)}><option value="COMPANY">Компании</option><option value="CONTACT">Контакты</option></select></label>
      <label>Системный ключ<input value={form.fieldKey} onChange={event => setForm(value => ({ ...value, fieldKey: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} pattern="[a-z][a-z0-9_]*" placeholder="service_category" required disabled={Boolean(editingId)} /></label>
      <label>Название поля<input value={form.label} onChange={event => setForm(value => ({ ...value, label: event.target.value }))} placeholder="Категория обслуживания" required /></label>
      <label>Тип<select value={form.valueType} onChange={event => setForm(value => ({ ...value, valueType: event.target.value as typeof value.valueType }))} disabled={Boolean(editingId)}><option value="TEXT">Текст</option><option value="NUMBER">Число</option><option value="BOOLEAN">Да / нет</option><option value="DATE">Дата</option><option value="DATETIME">Дата и время</option><option value="URL">Ссылка</option><option value="ENUM">Один вариант</option><option value="MULTI_ENUM">Несколько вариантов</option></select></label>
      <label>Владелец<select value={form.owner} onChange={event => setForm(value => ({ ...value, owner: event.target.value as typeof value.owner }))}><option value="PORTAL">Портал</option><option value="BITRIX24">Битрикс24</option></select></label>
      {form.owner === 'BITRIX24' && <label>Код поля Битрикс24<input value={form.bitrixFieldName} onChange={event => setForm(value => ({ ...value, bitrixFieldName: event.target.value.trim().toUpperCase() }))} placeholder="UF_CRM_..." required /></label>}
      {['ENUM', 'MULTI_ENUM'].includes(form.valueType) && <label className="wide">Варианты, по одному в строке<textarea rows={4} value={form.enumOptions} onChange={event => setForm(value => ({ ...value, enumOptions: event.target.value }))} required /></label>}
      <div className="directory-schema-admin__checks"><label><input type="checkbox" checked={form.isRequired} onChange={event => setForm(value => ({ ...value, isRequired: event.target.checked }))} /> Обязательное</label><label><input type="checkbox" checked={form.isActive} onChange={event => setForm(value => ({ ...value, isActive: event.target.checked }))} /> Активное</label></div>
      <div className="directory-schema-admin__form-actions"><button type="button" className="button ghost" onClick={() => setOpen(false)}>Отмена</button><button className="button primary" disabled={mutation.isPending}><Save /> {mutation.isPending ? 'Сохранение…' : 'Сохранить поле'}</button></div>
    </form>}
    <div className="directory-schema-admin__list">{isLoading ? <p>Загрузка полей…</p> : data.map(schema => <button key={schema.id} onClick={() => startEdit(schema)} className={!schema.is_active ? 'inactive' : ''}><span><b>{schema.label}</b><small>{schema.entity_type === 'COMPANY' ? 'Компания' : 'Контакт'} · {schema.value_type} · {schema.owner === 'BITRIX24' ? `Битрикс24${schema.bitrix_field_name ? ` / ${schema.bitrix_field_name}` : ''}` : 'Портал'}</small></span><em>{schema.is_active ? 'Активно' : 'Выключено'}</em><PencilLine /></button>)}{!isLoading && !data.length && <p>Пользовательские поля пока не настроены.</p>}</div>
  </section>;
}
